/**
 * Product Card Component
 * --------------------------------------------------------------------------------
 * @summary quickadd logic
 */
class ProductCard extends HTMLElement {
  //add a variable to control a functionality is available or not
  isNotifyMeAvailable = false;

  // Cache for prefetched swatch product data, keyed by handle
  _productCache = new Map();

  constructor() {
    super();

    this.selectors = {
      quickAddOpen: "[js-quick-add-open]",
      quickAddForm: "[js-quick-add-form]",
      quickAddClose: "[js-quick-add-close]",
      quickAddSubmit: "[js-quick-add-submit]",
      quickAddInput: "[js-quick-add-input]",
      quickAddError: "[js-quick-add-error]",
      quickAddSingleVariant: "[js-quick-add-single]",
      quickAddCell: "[js-quick-add-cell]",
      quickAddGrid: "[js-quick-add-grid]",
      quickAddProductData: "#quickadd-product-data",
      relatedCore: "[js-related-core-colors]",
      relatedSeasonal: "[js-related-seasonal-colors]",
      relatedGroup: "[js-related-colors-group]",
      productCardTitle: "[js-productcard-title]",
      productCardImgUrl: "[js-productcard-imgurl]",
      productCardTitleUrl: "[js-productcard-titleurl]",
      productCardId: "[js-productcard-id]",
      productCardPrice: "[js-productcard-price]",
      productCardComparePrice: "[js-productcard-compareprice]",
      productCardDiscount: "[js-productcard-discount]",
      productCardImages: "[js-productcard-images]",
      productBadge: "[js-product-badge]",
      productCardRemainingUrl: "[js-remaining-url]",
      productCardSaleMessage: "[js-productcard-sale-message]",
      productCardImagesOntop: "[js-productcard-images-ontop]",
      nostoScoreBadge: "[js-nosto-score-badge]",
    };

    // Cache DOM elements
    this.cachedElements = {};
    this.cartDrawer = null;
  }

  connectedCallback() {
    this._cacheElements();
    this._initImageSwiper();
    this._initQuickAdd();
    this._initRelatedSeasonalColors();
    this._initNostoScores();
  }

  disconnectedCallback() {
    // Cleanup event listeners
    if (this._swiperHandlers) {
      const { wrapper, enter, leave } = this._swiperHandlers;
      wrapper.removeEventListener("mouseenter", enter);
      wrapper.removeEventListener("mouseleave", leave);
    }

    // Destroy swiper instance
    if (this._swiperInstance) {
      this._swiperInstance.destroy(true, true);
      this._swiperInstance = null;
    }
  }

  _cacheElements() {
    this.cachedElements.quickAddOpen = this.querySelector(
      this.selectors.quickAddOpen,
    );
    this.cachedElements.quickAddForm = this.querySelector(
      this.selectors.quickAddForm,
    );
    this.cachedElements.quickAddClose = this.querySelector(
      this.selectors.quickAddClose,
    );
    this.cachedElements.quickAddSingleVariant = this.querySelector(
      this.selectors.quickAddSingleVariant,
    );
    this.cachedElements.productCardImagesOntop = this.querySelector(
      this.selectors.productCardImagesOntop,
    );
    this.cartDrawer = document.querySelector("#CartDrawer");
  }

  _initImageSwiper() {
    const swiperWrapper = this.querySelector(".product-image-carousel");
    if (!swiperWrapper || !window.Swiper) return;

    // --- 1. DEBOUNCE FOR MOUSE ---
    let hoverTimeout;
    const debounceHover = (callback, delay = 100) => {
      clearTimeout(hoverTimeout);
      hoverTimeout = setTimeout(callback, delay);
    };

    // --- 2. INIT SWIPER ---
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;

    let config = {
      slidesPerView: 1,
      resistanceRatio: 0,
      touchReleaseOnEdges: false,
      loop: false,
      // Default effect is 'slide', so we don't need to state it
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: false,
      },
    };

    if (isDesktop) {
      config = {
        ...config, // Keep the base settings
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        allowTouchMove: false, // Disable swiping on desktop
        navigation: {
          nextEl: this.querySelector(".swiper-button-next"),
          prevEl: this.querySelector(".swiper-button-prev"),
        },
      };
    }

    const swiper = new window.Swiper(swiperWrapper, config);

    // --- 3. DEFERRED IMAGE LOADER ---
    // Images beyond the first use data-src to avoid loading hidden Swiper slides.
    // Call this before showing a slide so the image is ready.
    const loadDeferredImage = (img) => {
      if (img && img.dataset.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
    };

    // Preload all remaining deferred images once the user has started interacting,
    // so subsequent slide transitions feel instant.
    const loadAllDeferredImages = () => {
      swiper.slides.forEach((img) => loadDeferredImage(img));
    };

    // On first touch, eagerly load all deferred images so they are already
    // fetching while the user is still dragging — prevents a blank slide on release.
    swiper.on("touchStart", () => {
      loadAllDeferredImages();
    });

    swiper.on("slideChange", () => {
      const active = swiper.slides[swiper.activeIndex];
      loadDeferredImage(active);
      // Preload the next slide so it's ready before the transition
      const next = swiper.slides[swiper.activeIndex + 1];
      if (next) loadDeferredImage(next);
    });

    // --- 4. BUTTON LOGIC ---
    const navButtons = [
      this.querySelector(".swiper-button-next"),
      this.querySelector(".swiper-button-prev"),
    ];

    navButtons.forEach((btn) => {
      if (btn) {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      }
    });

    this._swiperInstance = swiper;

    // --- 5. MOUSE HANDLERS (DESKTOP) ---
    const handleMouseEnter = () => {
      debounceHover(() => {
        if (swiper.slides.length > 1) {
          // Preload the second image before fading to it
          loadDeferredImage(swiper.slides[1]);
          // Kick off loading for any remaining slides in the background
          loadAllDeferredImages();
          swiper.slideTo(1, 300);
        }
      });
    };

    const handleMouseLeave = () => {
      debounceHover(() => {
        swiper.slideTo(0, 300);
      });
    };

    // --- 6. LONG PRESS HANDLERS (MOBILE) ---
    let pressTimer;

    const handleTouchStart = () => {
      // Start the timer when finger touches screen
      pressTimer = setTimeout(() => {
        if (swiper.slides.length > 1) {
          loadDeferredImage(swiper.slides[1]);
          loadAllDeferredImages();
          swiper.slideTo(1, 300);
        }
        console.log("Long Press Triggered");
      }, 600); // 600ms wait time
    };

    const handleTouchEnd = () => {
      // Finger lifted
      clearTimeout(pressTimer);
      // ACTION: Return to 1st slide
      swiper.slideTo(0, 300);
    };

    const handleTouchMove = () => {
      // CRITICAL: If they drag/scroll/swipe, cancel the long press immediately
      clearTimeout(pressTimer);
    };

    // --- 7. ATTACH LISTENERS ---
    // Only attach mouseenter/mouseleave events on desktop (not on mobile)
    if (window.innerWidth > 768) {
      swiperWrapper.addEventListener("mouseenter", handleMouseEnter, {
        passive: true,
      });
      swiperWrapper.addEventListener("mouseleave", handleMouseLeave, {
        passive: true,
      });
    }

    // Touch (Add these)
    // swiperWrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
    // swiperWrapper.addEventListener('touchend', handleTouchEnd, { passive: true });
    // swiperWrapper.addEventListener('touchmove', handleTouchMove, { passive: true });

    // --- 8. CLEANUP STORAGE ---
    this._swiperHandlers = {
      wrapper: swiperWrapper,
      enter: handleMouseEnter,
      leave: handleMouseLeave, // Add to cleanup
    };
  }

  _initQuickAdd() {
    const { quickAddOpen, quickAddClose, quickAddSingleVariant } = this.cachedElements;
    if (quickAddClose) {
      quickAddClose.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._hideForm();
      });
    }

    if (quickAddOpen) {
      this.addEventListener("mouseleave", this._hideForm.bind(this));
      quickAddOpen.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (window.innerWidth <= 768) {
          this._dataToQuickAddDrawer();
        } else {
          this._showForm();
        }
      });
    } else if (quickAddSingleVariant) {
      quickAddSingleVariant.addEventListener(
        "click",
        this._submitSingle.bind(this),
      );
    }

    // Event delegation for quick-add-cell clicks
    this.addEventListener("click", (e) => {
      const quickAddCell = e.target.closest(this.selectors.quickAddCell);
      if (quickAddCell) {
        this._handleQuickAddCell(e, quickAddCell);
      }
    });
  }

  _handleQuickAddCell(e, cell) {
    e.preventDefault();

    if (cell.classList.contains("out-of-stock")) {
      // If Notify Me is not available, return
      if (!this.isNotifyMeAvailable) {
        return;
      }

      // Define the variables based on the clicked cell
      // Note: Make sure your HTML element has data-name="Product Name"
      const pname = cell
        .closest("product-card")
        .querySelector(".product-card__title-link").textContent;
      const varId = cell.getAttribute("data-variantid");
      const prodId = cell.getAttribute("data-productid");
      const optionName = cell.textContent;

      // Find the popup elements
      const notifyPopup = document.querySelector(".collection_notify_popup");
      const overlay = document.querySelector(".notify-overlay");
      const klaviyoForm = notifyPopup
        ? notifyPopup.querySelector(".form-klaviyo-bis")
        : null;

      if (notifyPopup) {
        // Add active class to popup
        notifyPopup.classList.add("active-notify");

        // Set Product Name
        const nameElement = notifyPopup.querySelector(".Product_name");
        if (nameElement) {
          if (optionName) {
            nameElement.textContent = `${pname} - ${optionName}`;
          } else {
            nameElement.textContent = pname;
          }
        }

        // Set Klaviyo Attributes
        if (klaviyoForm) {
          klaviyoForm.setAttribute("data-variant-id", varId);
          klaviyoForm.setAttribute("data-product-id", prodId);
        }

        // Handle Body and Overlay classes
        if (overlay) overlay.classList.add("active-notify");
        document.body.classList.add("active-notify");
      }

      return;
    }

    const variantId = cell.getAttribute("data-variantId");
    if (!variantId) {
      console.error("No variant ID found");
      return;
    }

    this._addToCart(variantId);
  }

  _dataToQuickAddDrawer() {
    const productDataEl = this.querySelector(
      this.selectors.quickAddProductData,
    );
    if (!productDataEl) return;

    const product = JSON.parse(productDataEl.textContent);
    const drawer = document.querySelector("#ProductCardQuickAdd");
    if (!drawer) return;

    const titleEl = drawer.querySelector("[js-quickadd-drawer-title]");
    const variantsEl = drawer.querySelector("[js-quickadd-drawer-variants]");

    if (titleEl) titleEl.textContent = product.title;

    if (titleEl) titleEl.setAttribute("data-product-id", product.id);

    if (variantsEl) {
      variantsEl.innerHTML = product.variants
        .map((variant) => {
          const stockClass = product.prevent_atc
            ? "out-of-stock"
            : variant.available
              ? ""
              : "out-of-stock";
          return `<div class="product-variant-option ${stockClass}" data-variant-id="${variant.id}" js-quickadd-drawer-variant-option>${variant.option1}</div>`;
        })
        .join("");

      // Add loading spinner inside variants
      variantsEl.innerHTML += `
        <div class="quick-add-wrapper-inner" style="z-index: -1;" js-product-card-spinner>
          <loading-spinner style="--color: #333; --width: 24px; --height: 24px; --background: rgba(255,255,255);"></loading-spinner>
        </div>
      `;
    }

    drawer.open();
  }

  _showForm() {
    const form = this.cachedElements.quickAddForm;
    if (form) form.classList.add("translate-y");
    const productCardImagesOntop = this.cachedElements.productCardImagesOntop;
    if (productCardImagesOntop) productCardImagesOntop.classList.add("form-visible");
  }

  _hideForm() {
    const form = this.cachedElements.quickAddForm;
    if (form) form.classList.remove("translate-y");
    const productCardImagesOntop = this.cachedElements.productCardImagesOntop;
    if (productCardImagesOntop) productCardImagesOntop.classList.remove("form-visible");
  }

  _submitForm() {
    const checkedInput = this.querySelector(
      `${this.selectors.quickAddInput}:checked`,
    );
    const errorEl = this.querySelector(this.selectors.quickAddError);

    if (!checkedInput) {
      if (errorEl) {
        errorEl.classList.remove("hidden");
        errorEl.classList.add("block");
      }
      return;
    }

    if (errorEl) {
      errorEl.classList.add("hidden");
      errorEl.classList.remove("block");
    }

    this._addToCart(checkedInput.value, true);
  }

  _submitSingle(e) {
    const variantId = e.target.dataset.variantId;
    if (!variantId) {
      console.error("No variant ID found");
      return;
    }

    const cart = document.querySelector("cart-drawer");
    if (cart) {
      cart.setActiveElement(document.activeElement);
    }

    this._addToCart(variantId, true);
  }

  /**
   * Common method to add items to cart
   * @param {string} variantId - The variant ID to add
   * @param {boolean} reloadNosto - Whether to reload Nosto cart
   */
  async _addToCart(variantId, reloadNosto = false) {
    if (!variantId) {
      console.error("No variant ID provided");
      return;
    }

    // Show loading spinner
    const spinnerWrapper = this.querySelector("[js-product-card-spinner]");
    const spinner = this.querySelector("loading-spinner");
    if (spinnerWrapper && spinner) {
      spinnerWrapper.style.zIndex = 3;
      spinner.setAttribute("loading", "");
    }

    try {
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      const data = await response.json();

      if (data.status) {
        this._handleErrorMessage(data.description);
        // Hide spinner on error
        if (spinnerWrapper && spinner) {
          spinnerWrapper.style.zIndex = -1;
          spinner.removeAttribute("loading");
        }
        return;
      }

      const cartDrawer = document.querySelector("#CartDrawer");
      const isCartOpen = cartDrawer.classList.contains("open");

      if (cartDrawer && isCartOpen && window.renderCart) {
        await window.renderCart();
      }

      if (reloadNosto && window.Nosto) {
        window.Nosto.reloadCart();
      }

      if (this.cartDrawer) {
        this.cartDrawer.open();
      }

      // Hide spinner on success
      if (spinnerWrapper && spinner) {
        spinnerWrapper.style.zIndex = -1;
        spinner.removeAttribute("loading");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  }

  _handleErrorMessage(message) {
    console.error("Cart error:", message);
    // Add user-facing error handling if needed
  }

  async _initRelatedSeasonalColors() {
    const containers = this.querySelectorAll(this.selectors.relatedGroup);
    if (containers.length === 0) return;

    const firstContainer = containers[0];
    const currentProductHandle = firstContainer.getAttribute("data-handle");
    const relatedColorsGroup = firstContainer.getAttribute("data-group");
    const finalColor = firstContainer.getAttribute("data-color");

    // if (!relatedColorsGroup) return;

    if (currentProductHandle && !relatedColorsGroup) {
      try {
        const colorEntry = window.color_data.find((color) => {
          if (color.name == finalColor.toLowerCase().toString()) {
            return color;
          }
        });

        if (!colorEntry) return;

        const productJSON = [
          {
            handle: currentProductHandle,
            meta_swatch: colorEntry
          },
        ];

        const { orderedList, remainingCount } = this._buildSwatchList(
          productJSON,
          currentProductHandle,
        );

        const relatedColorsMarkup = this._renderSwatches(
          orderedList,
          currentProductHandle,
          remainingCount,
        );

        containers.forEach((container) => {
          container.innerHTML = relatedColorsMarkup;
        });

        this._attachSwatchListeners();

        return;
      } catch (error) {
        console.error("Error loading related seasonal colors:", error);
      }
    } else {
      try {
        const targetURL = `/collections/all/${relatedColorsGroup}/?view=json`;
        let relatedColorsJSON = await this._getRelatedColorJSON(targetURL);

        if (relatedColorsJSON.length === 0) return;

        relatedColorsJSON = relatedColorsJSON.filter( product => product.available == true );

        const { orderedList, remainingCount } = this._buildSwatchList(
          relatedColorsJSON,
          currentProductHandle,
        );

        const relatedColorsMarkup = this._renderSwatches(
          orderedList,
          currentProductHandle,
          remainingCount,
        );

        containers.forEach((container) => {
          container.innerHTML = relatedColorsMarkup;
        });

        this._attachSwatchListeners();
      } catch (error) {
        console.error("Error loading related seasonal colors:", error);
      }
    }
  }

  _buildSwatchList(products, currentHandle) {
    const currentProduct = products.find((p) => p.handle === currentHandle);
    const otherProducts = products.filter((p) => p.handle !== currentHandle);

    const orderedList = currentProduct ? [currentProduct] : [];
    let maxSwatches;
    if (this.closest("#CartDrawer")) {
      maxSwatches = window.innerWidth <= 767 ? 3 : 4;
    } else {
      maxSwatches = window.innerWidth <= 767 ? 4 : 5;
    }
    const remainingSlots = maxSwatches - orderedList.length;
    const visibleOthers = otherProducts.slice(0, remainingSlots);

    orderedList.push(...visibleOthers);

    return {
      orderedList,
      remainingCount: otherProducts.length - visibleOthers.length,
    };
  }

  _renderSwatches(orderedList, currentHandle, remainingCount) {
    const swatches = orderedList
      .map((product) => {
        const swatch = this._createSwatchHTML(product);
        const isActive = product.handle === currentHandle ? "active" : "";
        return `<div data-handle="${product.handle}" class="product-card-swatch ${isActive}" aria-label="${product.color}">${swatch}</div>`;
      })
      .join("");

    const remainingHTML =
      remainingCount > 0
        ? `<a href="/products/${currentHandle}" class="product-card-remaining" js-remaining-url>+${remainingCount}</a>`
        : "";

    return swatches + remainingHTML;
  }

  _createSwatchHTML(product) {

    if (product.meta_swatch && product.meta_swatch.color_code) {
      return `<div class="product-card-swatch-inner" style="background: ${product.meta_swatch.color_code};"></div>`;
    } else if (product.meta_swatch && product.meta_swatch.linear_gradient) {
      return `<div class="product-card-swatch-inner" style="background: ${product.meta_swatch.linear_gradient};"></div>`;
    } else if (product.meta_swatch && product.meta_swatch.image) {
      return `<div class="product-card-swatch-inner" style="background: url(${product.meta_swatch.image});"></div>`;
    } else {
      return `<div class="product-card-swatch-inner" style="background: #000;"></div>`;
    }
  }

  _attachSwatchListeners() {
    this.querySelectorAll(".product-card-swatch").forEach((swatch) => {
      // Prefetch on hover so data + first image are ready before the click
      swatch.addEventListener("mouseenter", () => {
        const handle = swatch.getAttribute("data-handle");
        if (handle) this._prefetchSwatch(handle);
      });

      swatch.addEventListener("click", (e) => {
        e.preventDefault();
        const handle = swatch.getAttribute("data-handle");
        if (handle) {
          this._swatchClicked(handle);
        }
      });
    });
  }

  async _prefetchSwatch(handle) {
    const relatedGroup = this.querySelector(this.selectors.relatedGroup);
    if (!relatedGroup) return;

    const originalHandle = relatedGroup.getAttribute("data-handle");
    if (handle === originalHandle) return;
    if (this._productCache.has(handle)) return;

    try {
      const response = await fetch(`/products/${handle}/?view=swatch`);
      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, "text/html");
      const productInfoEl = parsedHTML.querySelector("[js-product-json]");
      if (!productInfoEl) return;

      const productInfo = JSON.parse(productInfoEl.textContent);
      this._productCache.set(handle, productInfo);

      // Preload all images so they are in browser cache by click time
      if (productInfo.images) {
        productInfo.images.forEach((src) => {
          const img = new Image();
          img.src = src;
        });
      }
    } catch (_) {
      // Silent fail — prefetch is best-effort
    }
  }

  async _getRelatedColorJSON(url) {
    try {
      const response = await fetch(url);
      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, "text/html");
      const jsonContainer = parsedHTML.querySelector("[js-collection-json]");
      return jsonContainer ? JSON.parse(jsonContainer.textContent) : [];
    } catch (error) {
      console.error("Error fetching related colors:", error);
      return [];
    }
  }

  async _swatchClicked(handle) {
    const relatedGroup = this.querySelector(this.selectors.relatedGroup);
    if (!relatedGroup) return;

    const originalProductHandle = relatedGroup.getAttribute("data-handle");
    if (handle === originalProductHandle) return;

    // Use prefetched data if available — avoids waiting for network on click
    if (this._productCache.has(handle)) {
      this._updateProductCard(this._productCache.get(handle), handle);
      return;
    }

    try {
      const targetURL = `/products/${handle}/?view=swatch`;
      const response = await fetch(targetURL);
      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, "text/html");
      const productInfoEl = parsedHTML.querySelector("[js-product-json]");

      if (!productInfoEl) {
        console.error("Product info not found");
        return;
      }

      const productInfo = JSON.parse(productInfoEl.textContent);
      this._productCache.set(handle, productInfo);
      this._updateProductCard(productInfo, handle);
    } catch (error) {
      console.error("Error loading product swatch:", error);
    }
  }

  _updateProductCard(productInfo, handle) {
    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      // Cache elements for batch updates
      const elements = {
        title: this.querySelector(this.selectors.productCardTitle),
        imgUrl: this.querySelector(this.selectors.productCardImgUrl),
        titleUrl: this.querySelector(this.selectors.productCardTitleUrl),
        productId: this.querySelector(this.selectors.productCardId),
        price: this.querySelector(this.selectors.productCardPrice),
        comparePrice: this.querySelector(
          this.selectors.productCardComparePrice,
        ),
        discount: this.querySelector(this.selectors.productCardDiscount),
        badge: this.querySelector(this.selectors.productBadge),
        images: this.querySelectorAll(
          `${this.selectors.productCardImages} img`,
        ),
        quickAddGrid: this.querySelector(this.selectors.quickAddGrid),
        productData: this.querySelector(this.selectors.quickAddProductData),
        relatedGroup: this.querySelector(this.selectors.relatedGroup),
        remainingUrl: this.querySelector(
          this.selectors.productCardRemainingUrl,
        ),
        saleMessage: this.querySelector(this.selectors.productCardSaleMessage),
        scoreBadge: this.querySelector(this.selectors.nostoScoreBadge),
      };

      this._performProductCardUpdate(elements, productInfo, handle);
    });
  }

  _performProductCardUpdate(elements, productInfo, handle) {
    // Update text content
    if (elements.title) elements.title.textContent = productInfo.title;
    if (elements.price) elements.price.textContent = productInfo.price;

    // Update URLs
    if (elements.imgUrl) elements.imgUrl.href = productInfo.url;
    if (elements.titleUrl) elements.titleUrl.href = productInfo.url;

    // Update ID
    if (elements.productId) elements.productId.id = productInfo.id;

    // Update handle
    if (elements.relatedGroup) {
      elements.relatedGroup.setAttribute("data-handle", productInfo.handle);
    }

    // Update compare price and discount
    const hasComparePrice =
      productInfo.compare_at_price && productInfo.compare_at_price !== "";
    if (elements.comparePrice) {
      if (hasComparePrice) {
        elements.comparePrice.textContent = productInfo.compare_at_price;
        elements.comparePrice.classList.remove("hidden");
      } else {
        elements.comparePrice.classList.add("hidden");
      }
    }

    if (elements.discount) {
      if (hasComparePrice) {
        elements.discount.textContent = productInfo.price_save;
        elements.discount.classList.remove("hidden");
      } else {
        elements.discount.classList.add("hidden");
      }
    }

    // Update badge
    if (elements.badge) {
      if (productInfo.badge && productInfo.badge !== "") {
        elements.badge.innerHTML = productInfo.badge;
      }
    }

    // Update images
    if (
      elements.images &&
      productInfo.images &&
      productInfo.images.length > 0
    ) {
      // Store container height to prevent layout shift
      const imageContainer = this.querySelector(
        ".product-card__image-container",
      );
      const containerHeight = imageContainer
        ? imageContainer.offsetHeight
        : null;

      // Maintain container height during update
      if (imageContainer && containerHeight) {
        imageContainer.style.minHeight = `${containerHeight}px`;
      }

      elements.images.forEach((img, index) => {
        if (productInfo.images[index]) {
          const imageUrl = productInfo.images[index];

          // Resolve the final URL (pick the smallest size from a srcset-style URL)
          let resolvedUrl = imageUrl;
          if (imageUrl.includes("_") && imageUrl.match(/_\d+x\d+\./)) {
            const match = imageUrl.match(
              /^(.+)_\d+x\d+(\.(jpg|jpeg|png|webp|gif).*)$/i,
            );
            if (match) resolvedUrl = `${match[1]}_300x${match[2]}`;
          } else if (imageUrl.includes("?width=")) {
            resolvedUrl = `${imageUrl.split("?")[0]}?width=300`;
          }

          img.alt = productInfo.title;

          if (index === 0) {
            img.loading = "eager";
            delete img.dataset.src;
            img.src = resolvedUrl;
            img.onload = () => {
              if (imageContainer) imageContainer.style.minHeight = "";
            };
          } else {
            // Load all non-first images eagerly too so they are already fetched
            // by the time the user hovers to see the second slide.
            img.loading = "eager";
            delete img.dataset.src;
            img.src = resolvedUrl;
          }
        }
      });

      // Update Swiper after images are set
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (this._swiperInstance) {
            this._swiperInstance.update();
            this._swiperInstance.slideTo(0, 0);
          }
          // Remove min-height constraint after a delay
          if (imageContainer) {
            setTimeout(() => {
              imageContainer.style.minHeight = "";
            }, 300);
          }
        }, 100);
      });
    }

    // Update quick add grid
    if (elements.quickAddGrid && productInfo.variants) {
      elements.quickAddGrid.innerHTML =
        `<div class="quick-add-wrapper-inner" style="z-index: -1;" js-product-card-spinner>
            <loading-spinner style="--color: #333; --width: 24px; --height: 24px; --background: rgba(255,255,255,0.95);"></loading-spinner>
        </div>` +
        productInfo.variants
          .map((variant) => {
            const stockClass = variant.available ? "" : "out-of-stock";
            return `<div class="quick-add-grid-cell ${stockClass}" data-variantId="${variant.id}" js-quick-add-cell>${variant.option1}</div>`;
          })
          .join("");
    }

    // Update product data JSON
    if (elements.productData && productInfo.variants) {
      elements.productData.textContent = JSON.stringify({
        title: productInfo.title,
        id: productInfo.id,
        variants: productInfo.variants,
      });
    }

    // Update active swatch
    const activeSwatch = this.querySelector(".product-card-swatch.active");
    const newActiveSwatch = this.querySelector(
      `.product-card-swatch[data-handle="${handle}"]`,
    );
    if (activeSwatch) activeSwatch.classList.remove("active");
    if (newActiveSwatch) newActiveSwatch.classList.add("active");

    //Update remaining url
    if (elements.remainingUrl) elements.remainingUrl.href = productInfo.url;

    // Update sale message
    if (elements.saleMessage) {
      if (productInfo.sale_message && productInfo.sale_message !== "") {
        elements.saleMessage.innerHTML = productInfo.sale_message;
      }
    }
    if (elements.badge) {
      if (productInfo.badge && productInfo.badge !== "") {
        elements.badge.innerHTML = productInfo.badge;
      }
    }
    if (elements.scoreBadge) {
      this._initNostoScores(productInfo.id);
    }
  }

  async _initNostoScores(id = this.getAttribute("data-nosto-product-id")) {
    const BUYS_THRESHOLD = 10;
    const VIEWS_THRESHOLD = 220;
    const NOSTO_TOKEN = "y4G0FMVcgkjjtm6AqmDztohLO5UMXqMiHmdkGjQyvqnlzmiKQ8FWEP7jJ74Gmo4w";

    // Resolve product ID: check self first, then parent nosto-dynamic-card
    let productId = id;

    if (!productId) {
      const nostoParent = this.closest("nosto-dynamic-card");
      if (nostoParent) {
        productId = nostoParent.getAttribute("data-nosto-product-id");
        if (productId) {
          this.setAttribute("data-nosto-product-id", productId);
        }
      }
    }

    if (!productId) return;

    const badgeEl = this.querySelector(this.selectors.nostoScoreBadge);
    if (!badgeEl) return;

    const query = `{
      product(id: "${productId}") {
        scores {
          day {
            buys
            views
          }
        }
      }
    }`;

    try {
      const response = await fetch("https://api.nosto.com/v1/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Basic " + btoa(":" + NOSTO_TOKEN),
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) return;

      const data = await response.json();
      const scores = data?.data?.product?.scores?.day;
      if (!scores) return;

      const { buys, views } = scores;

      const fireSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5.69988 1.03027C5.69988 1.03027 7.50988 2.37027 7.50988 4.31027C7.50988 5.31027 7.21988 6.33027 7.33988 7.33027C7.44988 8.24027 8.20988 8.87027 9.14988 8.83027C9.35988 8.83027 9.57988 8.77027 9.75988 8.65027C10.0399 8.46027 10.1799 8.13027 10.2299 7.80027C10.2999 7.35027 10.2099 6.22027 9.61988 6.10027C10.1399 6.20027 10.6999 6.75027 10.9999 7.15027C11.7299 8.12027 11.7199 9.51027 11.2299 10.5903C10.5399 12.1003 8.84988 12.9703 7.20988 12.9703C7.31988 12.9703 7.54988 12.4703 7.57988 12.4003C7.78988 11.9803 7.88988 11.5503 7.86988 11.0803C7.84988 10.5903 7.71988 10.1103 7.50988 9.68027C7.40988 9.47027 6.89988 8.45027 6.57988 8.77027C6.02988 9.31027 5.77988 10.0603 5.72988 10.8103C5.70988 11.1503 5.72988 11.4903 5.76988 11.8203C5.80988 12.1403 5.96988 12.4903 5.96988 12.8003C5.52988 12.8103 5.00988 12.6903 4.59988 12.5403C3.85988 12.2903 3.21988 11.7503 2.82988 11.0803C2.38988 10.3203 2.34988 9.34027 2.48988 8.49027C2.58988 7.86027 2.84988 7.25027 3.26988 6.77027C3.99988 5.93027 5.02988 5.50027 5.56988 4.49027C6.13988 3.43027 6.23988 2.12027 5.68988 1.03027H5.69988Z" stroke="black" stroke-width="0.87" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      const eyeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><g clip-path="url(#clip0_1287_7888)"><path d="M13.1701 6.99992C11.9301 4.86992 9.64008 3.41992 7.00008 3.41992C4.36008 3.41992 2.07008 4.86992 0.830078 6.99992C2.07008 9.12992 4.36008 10.5799 7.00008 10.5799C9.64008 10.5799 11.9301 9.12992 13.1701 6.99992Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 8.5C7.82843 8.5 8.5 7.82843 8.5 7C8.5 6.17157 7.82843 5.5 7 5.5C6.17157 5.5 5.5 6.17157 5.5 7C5.5 7.82843 6.17157 8.5 7 8.5Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_1287_7888"><rect width="14" height="14" fill="white"/></clipPath></defs></svg>`;

      if (buys >= BUYS_THRESHOLD && views >= VIEWS_THRESHOLD) {
        badgeEl.innerHTML = `${fireSvg}<div class="score-text">${buys} Sold in last 24hrs</div>`;
        badgeEl.classList.add("active");
      } else if (views >= VIEWS_THRESHOLD) {
        badgeEl.innerHTML = `${eyeSvg}<div class="score-text">${views} Views in last 24hrs</div>`;
        badgeEl.classList.add("active");
      } else {
        badgeEl.classList.remove("active");
        badgeEl.innerHTML = "";
      }
    } catch (_) {
      // Silent fail — score badge is non-critical
    }
  }
}

customElements.define("product-card", ProductCard);
